// ════════════════════════════════════════════════════════════════════════════
//  pages/Fiscal/Averbacao.tsx
//  Histórico de averbação de CT-e e MDF-e junto à seguradora
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Shield, XCircle, Search, RefreshCw, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useAverbacao } from '@/features/fiscal/transporte/hooks/useAverbacao';
import type { FiltrosAverbacao } from '@/features/fiscal/transporte/services/averbacaoService';
import { useDebounce } from '@/hooks/useDebounce';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import type { AverbarRequest, MovAverbacaoDto } from '@/features/fiscal/transporte/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useEmpresas } from '@/features/empresa/hooks/useEmpresa';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';


type StatusAverb = 'averbado' | 'pendente' | 'cancelado' | 'erro';

const statusConfig: Record<StatusAverb, { label: string; className: string }> = {
  averbado:  { label: 'Averbado',  className: 'bg-green-500/10 text-green-600'   },
  pendente:  { label: 'Pendente',  className: 'bg-yellow-500/10 text-yellow-600' },
  cancelado: { label: 'Cancelado', className: 'bg-muted text-muted-foreground'   },
  erro:      { label: 'Erro',      className: 'bg-red-500/10 text-red-600'       },
};

// O backend grava o nome do StatusAverbacaoEnum (Processado = averbado com sucesso).
function resolveStatus(statusAverbacao: string): StatusAverb {
  const s = statusAverbacao?.toLowerCase() ?? '';
  if (s === 'processado' || s === 'averbado') return 'averbado';
  if (s === 'pendente') return 'pendente';
  if (s === 'cancelado') return 'cancelado';
  return 'erro';
}

// Data LOCAL yyyy-MM-dd (toISOString é UTC e "vira o dia" depois das 21h).
const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const primeiroDiaMes = () => { const d = new Date(); return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1)); };

const TODOS = '__todos__';

const chaveResumida = (chave: string | null) =>
  chave ? `...${chave.slice(-12)}` : '—';

const FORM_AVERBAR = {
  chaveAcesso: '',
  tipoDocumento: 'CTe' as 'CTe' | 'MDFe',
  xmlConteudo: '',
  valorCarga: 0,
  numeroCte: 0,
  serieCte: '',
  empresaId: 1,
};

const Averbacao = () => {
  const [busca,         setBusca]         = useState('');
  const [dtInicio,      setDtInicio]      = useState(primeiroDiaMes);
  const [dtFim,         setDtFim]         = useState(() => isoLocal(new Date()));
  const [statusFiltro,  setStatusFiltro]  = useState(TODOS);
  const [expandedId,    setExpandedId]    = useState<number | null>(null);
  const buscaDebounced = useDebounce(busca, 400);
  const filtros: FiltrosAverbacao = {
    busca: buscaDebounced, dtInicio, dtFim,
    status: statusFiltro === TODOS ? undefined : statusFiltro,
  };
  const pag = usePaginacaoServidor({ resetarQuando: [filtros] });

  // Dialogs
  const [averbarOpen,   setAverbarOpen]   = useState(false);
  const [cancelandoDoc, setCanceladoDoc]  = useState<MovAverbacaoDto | null>(null);
  const [motivoCancel,  setMotivoCancel]  = useState('');

  // Form averbar
  const { user } = useAuth();
  const { data: empresas = [] } = useEmpresas();
  // Empresa padrão = a do usuário logado (antes era fixo 1).
  const formInicial = { ...FORM_AVERBAR, empresaId: user?.empresa.id ?? FORM_AVERBAR.empresaId };
  const [form, setForm] = useState<typeof FORM_AVERBAR>(formInicial);

  // Busca/período/status filtrados no servidor (antes a busca rodava só na página carregada).
  const { itens, total, totalPaginas, isLoading, averbar, cancelar } =
    useAverbacao(pag.pagina, pag.tamanhoPagina, filtros);
  const itensFiltrados = itens;

  const setF = <K extends keyof typeof FORM_AVERBAR>(k: K, v: (typeof FORM_AVERBAR)[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const handleAverbar = async () => {
    await averbar.mutateAsync(form as AverbarRequest);
    setAverbarOpen(false);
    setForm(formInicial);
  };

  const handleCancelar = () => {
    if (!cancelandoDoc) return;
    cancelar.mutate({ id: cancelandoDoc.id, motivo: motivoCancel });
    setCanceladoDoc(null);
    setMotivoCancel('');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Averbação</h1>
          <p className="text-muted-foreground mt-1">Averbação de CT-e e MDF-e junto à seguradora</p>
        </div>
        <Button className="gap-2" onClick={() => { setForm(formInicial); setAverbarOpen(true); }}>
          <Plus className="h-4 w-4" />
          Averbar Manualmente
        </Button>
      </div>

      {/* Os cards "Nesta página" foram retirados: contavam só a página e pareciam totais.
          O filtro de Situação abaixo dá a contagem real (rodapé). */}
      <Card>
        <CardHeader className="space-y-3">
          <CardTitle>Histórico de Averbações</CardTitle>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Averbação de</Label>
              <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">até</Label>
              <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
            </div>
            <div className="space-y-1 w-44">
              <Label className="text-xs">Situação</Label>
              <Select value={statusFiltro} onValueChange={setStatusFiltro}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todas</SelectItem>
                  <SelectItem value="Processado">Averbado</SelectItem>
                  <SelectItem value="Pendente">Pendente</SelectItem>
                  <SelectItem value="Rejeitado">Rejeitado</SelectItem>
                  <SelectItem value="Negado">Negado</SelectItem>
                  <SelectItem value="Duplicado">Duplicado</SelectItem>
                  <SelectItem value="Erro">Erro</SelectItem>
                  <SelectItem value="Cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 min-w-[220px] md:max-w-sm">
              <Label className="text-xs">Busca</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nº CT-e, chave, seguradora ou protocolo..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº CT-e</TableHead>
                  <TableHead>Série</TableHead>
                  <TableHead>Chave CT-e / MDF-e</TableHead>
                  <TableHead>Seguradora</TableHead>
                  <TableHead>Data Averbação</TableHead>
                  <TableHead className="text-right">Valor Carga</TableHead>
                  <TableHead className="text-center">Tentativas</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : itensFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Nenhuma averbação encontrada</TableCell>
                  </TableRow>
                ) : (
                  itensFiltrados.map((item) => {
                    const status = resolveStatus(item.statusAverbacao);
                    const cfg    = statusConfig[status];
                    const isExpanded = expandedId === item.id;
                    return (
                      <>
                        <TableRow key={item.id} className={isExpanded ? 'bg-muted/40' : undefined}>
                          <TableCell className="font-medium">{String(item.numeroCte).padStart(6, '0')}</TableCell>
                          <TableCell>{item.serieCte ?? '—'}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground"
                            title={(item.chaveCte ?? item.chaveMdfe) ?? undefined}>
                            {chaveResumida(item.chaveCte ?? item.chaveMdfe)}
                          </TableCell>
                          <TableCell className="max-w-[140px] truncate">{item.descricaoSeguradora ?? '—'}</TableCell>
                          <TableCell>{new Date(item.dtAverbacao).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell className="text-right">
                            {item.valorCarga.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </TableCell>
                          <TableCell className="text-center">{item.tentativasEnvio}</TableCell>
                          <TableCell>
                            <Badge className={cfg.className}>{cfg.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7"
                                onClick={() => setExpandedId(isExpanded ? null : item.id)}
                                title="Ver detalhes">
                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                              </Button>
                              {!item.isCancelado && (
                                <Button size="icon" variant="ghost"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => { setCanceladoDoc(item); setMotivoCancel(''); }}
                                  title="Cancelar averbação">
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${item.id}-detail`} className="bg-muted/20">
                            <TableCell colSpan={9} className="py-3 px-6">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Protocolo</p>
                                  <p className="font-mono">{item.protocolo ?? '—'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Tentativas de Envio</p>
                                  <p>{item.tentativasEnvio}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Última Tentativa</p>
                                  <p>{item.dtUltimaTentativa ? new Date(item.dtUltimaTentativa).toLocaleString('pt-BR') : '—'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Valor Averbação</p>
                                  <p>{item.valorAverbacao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="pt-2 border-t">
            <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />
          </div>
        </CardContent>
      </Card>

      {/* Dialog: Averbar Manualmente */}
      <Dialog open={averbarOpen} onOpenChange={(o) => { if (!o) setAverbarOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Averbar Manualmente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Chave de Acesso * (44 dígitos)</Label>
              <Input maxLength={44} value={form.chaveAcesso} onChange={(e) => setF('chaveAcesso', e.target.value)}
                placeholder="44 dígitos" />
            </div>
            <div className="space-y-1">
              <Label>Tipo de Documento *</Label>
              <Select value={form.tipoDocumento} onValueChange={(v) => setF('tipoDocumento', v as 'CTe' | 'MDFe')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CTe">CT-e</SelectItem>
                  <SelectItem value="MDFe">MDF-e</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Número CT-e *</Label>
                <Input type="number" min={0} value={form.numeroCte}
                  onChange={(e) => setF('numeroCte', Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label>Série CT-e</Label>
                <Input value={form.serieCte} onChange={(e) => setF('serieCte', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Valor da Carga (R$) *</Label>
                <Input type="number" min={0} step={0.01} value={form.valorCarga}
                  onChange={(e) => setF('valorCarga', Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label>Empresa *</Label>
                <Select value={form.empresaId ? String(form.empresaId) : ''}
                  onValueChange={(v) => setF('empresaId', Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
                  <SelectContent>
                    {empresas.map((e) => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nomeFantasia || e.razaoSocial}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAverbarOpen(false)}>Cancelar</Button>
            <Button onClick={handleAverbar}
              disabled={averbar.isPending || !form.chaveAcesso || form.numeroCte <= 0}>
              {averbar.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Averbando...</>
              ) : (
                <><Shield className="h-4 w-4 mr-2" />Averbar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Cancelar Averbação */}
      <Dialog open={!!cancelandoDoc} onOpenChange={(o) => { if (!o) setCanceladoDoc(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Averbação — CT-e {cancelandoDoc ? String(cancelandoDoc.numeroCte).padStart(6, '0') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Motivo do cancelamento</Label>
              <Input value={motivoCancel} onChange={(e) => setMotivoCancel(e.target.value)}
                placeholder="Descreva o motivo..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCanceladoDoc(null)}>Fechar</Button>
            <Button variant="destructive" onClick={handleCancelar} disabled={cancelar.isPending}>
              {cancelar.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cancelando...</>
              ) : (
                <><XCircle className="h-4 w-4 mr-2" />Confirmar Cancelamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Averbacao;
